import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./risk-assessment-form.i18n";
import type { ComponentFormProps } from "./types";

export type RiskAssessmentConfig = {
  title?: string;
  description?: string;
  /** QL-9 — strict opt-in; only an explicit `true` shows the derivation rows. */
  showAdjustments?: boolean;
};

type RiskAssessmentFormProps = ComponentFormProps<RiskAssessmentConfig>;

export function RiskAssessmentForm({ config, onChange }: RiskAssessmentFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="risk-title">{t.title}</Label>
        <Input
          id="risk-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="risk-description">{t.description}</Label>
        <Textarea
          id="risk-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="risk-show-adjustments" className="text-base">
            {t.showAdjustments}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showAdjustmentsHelp}</p>
        </div>
        <Switch
          id="risk-show-adjustments"
          checked={config.showAdjustments === true}
          onCheckedChange={(checked) => onChange({ ...config, showAdjustments: checked })}
        />
      </div>
    </div>
  );
}

export function validateRiskAssessmentConfig(_config: RiskAssessmentConfig): string | null {
  // Title and description are optional, no validation needed
  return null;
}

export function getDefaultRiskAssessmentConfig(): RiskAssessmentConfig {
  return {
    title: "Risk Assessment",
    description: "Your personal risk profile based on the questionnaire.",
    showAdjustments: false,
  };
}
